import {
  Component,
  computed,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgIcon } from '@ng-icons/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { AuthType } from '@shared/types/dto/credential/auth-type.enum';
import { HlmButton } from '@spartan-ng/helm/button';
import {
  HlmSelect,
  HlmSelectContent,
  HlmSelectItem,
  HlmSelectPortal,
  HlmSelectTrigger,
  HlmSelectValue,
} from '@spartan-ng/helm/select';
import { CredentialService } from '../../../core/services/credential.service';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Custom field type do Formly para selecionar uma credencial existente
 * (filtrada pelos supportedAuthTypes da step do conector) ou criar uma nova.
 * Select spartan (hlm-select) + botão "Nova".
 */
@Component({
  selector: 'evolve-credential-picker',
  imports: [
    ReactiveFormsModule,
    HlmButton,
    NgIcon,
    HlmSelect,
    HlmSelectContent,
    HlmSelectItem,
    HlmSelectPortal,
    HlmSelectTrigger,
    HlmSelectValue,
    TranslatePipe,
  ],
  template: `
    @if (props.label) {
      <label
        class="block text-sm font-medium text-foreground mb-1"
      >
        {{ props.label }}
        @if (props.required) {
          <span class="text-red-500">*</span>
        }
      </label>
    }
    <div class="flex gap-2 items-end">
      <hlm-select
        class="flex-1"
        [itemToString]="credentialLabel"
        [formControl]="formControl"
      >
        <hlm-select-trigger class="w-full">
          <hlm-select-value
            [placeholder]="
              props.placeholder ??
              ('shared.forms.credentialPicker.placeholder' | translate)
            "
          />
        </hlm-select-trigger>
        <hlm-select-content *hlmSelectPortal>
          @for (cred of credentialOptions(); track cred.id) {
            <hlm-select-item [value]="cred.id">{{ cred.name }}</hlm-select-item>
          }
        </hlm-select-content>
      </hlm-select>
      <button hlmBtn variant="secondary" type="button" (click)="openNewModal()">
        <ng-icon name="lucidePlus" />
        {{ 'shared.forms.credentialPicker.new' | translate }}
      </button>
    </div>
    @if (props.description) {
      <small class="text-muted-foreground">{{ props.description }}</small>
    }
  `,
})
export class CredentialPickerFieldComponent extends FieldType<FieldTypeConfig> {
  private credentialService = inject(CredentialService);

  supportedAuthTypes = computed<AuthType[]>(
    () =>
      ((this.props as Record<string, unknown>)[
        'supportedAuthTypes'
      ] as AuthType[]) ?? [],
  );

  // Nao usar o nome `options`: FieldType ja define `options: FormlyFormOptions`.
  credentialOptions = this.credentialService.filteredBy(
    this.supportedAuthTypes,
  );

  /** Mapeia o id selecionado para o nome da credencial no trigger. */
  credentialLabel = (value: unknown): string =>
    this.credentialOptions().find((c) => c.id === value)?.name ??
    String(value ?? '');

  openNewModal() {
    // Disparado via custom event no DOM — a implementacao concreta do modal
    // (CredentialFormModalComponent) e feita em outra unidade de trabalho.
    window.dispatchEvent(
      new CustomEvent('evolve:open-credential-modal', {
        detail: { supportedAuthTypes: this.supportedAuthTypes() },
      }),
    );
  }
}
